// floaterUtils.js — shared helpers (copy into both apps)
// Shared by the renderer and the editor. No JSX here.

// ─── Channel registry ─────────────────────────────────────────────────────────
// Adding a new channel = one entry here + one icon in FloaterIcons.jsx.

const enc = encodeURIComponent

export const CHANNELS = {
  whatsapp: {
    name: 'WhatsApp', color: '#25D366', prefill: true,
    targetLabel: 'Phone number, international format',
    placeholder: '66812345678',
    href: (t, text) => {
      const n = t.replace(/\D/g, '')
      return `https://wa.me/${n}${text ? `?text=${enc(text)}` : ''}`
    },
  },
  line: {
    name: 'LINE', color: '#06C755', prefill: true,
    targetLabel: 'LINE Official Account ID (@…) or a full line.me URL',
    placeholder: '@yourcompany',
    href: (t, text) => {
      if (/^https?:\/\//i.test(t)) return t
      const id = t.startsWith('@') ? t : `@${t}`
      // Prefilled text only works for Official Accounts
      return text
        ? `https://line.me/R/oaMessage/${enc(id)}/?${enc(text)}`
        : `https://line.me/R/ti/p/${enc(id)}`
    },
  },
  messenger: {
    name: 'Messenger', color: '#0084FF', prefill: false,
    targetLabel: 'Facebook page username', placeholder: 'yourpage',
    href: t => `https://m.me/${t.replace(/^@/, '')}`,
  },
  telegram: {
    name: 'Telegram', color: '#26A5E4', prefill: false,
    targetLabel: 'Telegram username', placeholder: 'yourcompany',
    href: t => `https://t.me/${t.replace(/^@/, '')}`,
  },
  wechat: {
    name: 'WeChat', color: '#07C160', prefill: false, qr: true,
    targetLabel: 'WeChat ID (shown under the QR code)', placeholder: 'yourwechatid',
    href: () => null,   // opens a QR modal instead
  },
  phone: {
    name: 'Call', color: '#2563EB', prefill: false, sameTab: true,
    targetLabel: 'Phone number', placeholder: '+66 81 234 5678',
    href: t => `tel:${t.replace(/[^\d+]/g, '')}`,
  },
  email: {
    name: 'Email', color: '#EA580C', prefill: true, subject: true, sameTab: true,
    targetLabel: 'Email address', placeholder: 'sales@example.com',
    href: (t, text, subject) => {
      const q = [subject && `subject=${enc(subject)}`, text && `body=${enc(text)}`].filter(Boolean).join('&')
      return `mailto:${t}${q ? `?${q}` : ''}`
    },
  },
  custom: {
    name: 'Link', color: '#374151', prefill: false,
    targetLabel: 'URL', placeholder: 'https://…',
    href: t => t,
  },
}

export const CHANNEL_TYPES = Object.keys(CHANNELS)

// ─── Defaults ─────────────────────────────────────────────────────────────────

export const CHANNEL_DEFAULTS = {
  enabled: true,
  type: 'whatsapp',
  target: '',
  label: {},
  prefill: {},
  subject: {},
  qrImage: '',
  iconUrl: '',
  bgColor: '',
  iconColor: '#ffffff',
  useContextTarget: false,
  devices: { desktop: true, mobile: true },
  schedule: { enabled: false, timezone: 'Asia/Bangkok', days: [1, 2, 3, 4, 5], from: '09:00', to: '18:00' },
}

export const FLOATER_DEFAULTS = {
  enabled: false,
  layout: 'auto',
  labelMode: 'hover',
  visibility: {
    desktop: true, mobile: true, breakpoint: 768,
    routeMode: 'exclude', routes: [],
    showAfterMs: 0, showAfterScrollPx: 0,
  },
  appearance: {
    position: 'right',
    offsetX: 24, offsetY: 24, mobileOffsetX: 16, mobileOffsetY: 16,
    size: 56, mobileSize: 52, iconSize: 28, mobileIconSize: 26,
    radius: '9999px', shadow: '0 4px 14px rgba(0,0,0,0.18)',
    gap: 12, zIndex: 900, pulse: false,
    labelBg: '#ffffff', labelColor: '#111827', labelFontSize: '0.875rem',
  },
  mainButton: { bgColor: 'var(--color-main)', iconColor: '#ffffff', icon: 'chat', iconUrl: '', label: {} },
  greeting: { enabled: false, text: {}, delayMs: 4000, showOnce: true },
  analytics: { event: 'floating_widget_click' },
  channels: [],
}

export function resolveConfig(raw) {
  const r = (raw && typeof raw.toObject === 'function' ? raw.toObject() : raw) || {}
  const d = FLOATER_DEFAULTS
  return {
    ...d,
    ...r,
    visibility: { ...d.visibility, ...r.visibility },
    appearance: { ...d.appearance, ...r.appearance },
    mainButton: { ...d.mainButton, ...r.mainButton },
    greeting:   { ...d.greeting, ...r.greeting },
    analytics:  { ...d.analytics, ...r.analytics },
    channels: (r.channels ?? []).map(c => ({
      ...CHANNEL_DEFAULTS,
      ...c,
      devices:  { ...CHANNEL_DEFAULTS.devices, ...c.devices },
      schedule: { ...CHANNEL_DEFAULTS.schedule, ...c.schedule },
    })),
  }
}

export function newChannel(type = 'whatsapp') {
  const id = globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
  return { ...structuredClone(CHANNEL_DEFAULTS), id, type }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function pickLocale(obj, lang, defaultLocale = 'en') {
  if (!obj) return ''
  if (typeof obj === 'string') return obj
  return obj[lang] || obj[defaultLocale] || obj.en || Object.values(obj).find(Boolean) || ''
}

// "Hi, I'm interested in {ref} – {url}" → unknown keys become empty
export function interpolate(text, vars = {}) {
  if (!text) return ''
  return text
    .replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? '').toString())
    .replace(/[ \t]{2,}/g, ' ')
    .trim()
}

const localeCode = l => (typeof l === 'string' ? l : l?.code ?? l?.locale)

export function stripLocale(pathname = '/', locales = []) {
  const codes = locales.map(localeCode).filter(Boolean)
  const seg = pathname.split('/')[1]
  if (seg && codes.includes(seg)) return pathname.slice(seg.length + 1) || '/'
  return pathname || '/'
}

// '*' matches anything (including '/'): '/property/*', '/admin*', '/contact'
function routeRegex(pattern) {
  const p = pattern.trim().replace(/\/+$/, '') || '/'
  const esc = p.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*')
  return new RegExp(`^${esc}/?$`, 'i')
}

export function isRouteVisible(path, mode = 'exclude', routes = []) {
  const list = (routes ?? []).map(r => r.trim()).filter(Boolean)
  if (!list.length) return true
  const matched = list.some(r => routeRegex(r).test(path))
  return mode === 'include' ? matched : !matched
}

export function isOpenNow(schedule, now = new Date()) {
  if (!schedule?.enabled) return true
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', {
        timeZone: schedule.timezone || 'UTC',
        weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(now).map(p => [p.type, p.value]),
    )
    const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday)
    if (!schedule.days?.includes(day)) return false
    const mins = Number(parts.hour) * 60 + Number(parts.minute)
    const toMin = s => { const [h, m] = (s || '0:0').split(':').map(Number); return h * 60 + (m || 0) }
    const from = toMin(schedule.from), to = toMin(schedule.to)
    return from <= to ? mins >= from && mins < to : mins >= from || mins < to
  } catch {
    return true   // bad timezone → fail open
  }
}

export function resolveTarget(channel, targets = {}) {
  return ((channel.useContextTarget && targets?.[channel.type]) || channel.target || '').trim()
}

export function buildChannelHref(channel, { lang, defaultLocale, vars = {}, targets = {} } = {}) {
  const def = CHANNELS[channel.type] ?? CHANNELS.custom
  const target = resolveTarget(channel, targets)
  if (!target) return null
  const text = def.prefill ? interpolate(pickLocale(channel.prefill, lang, defaultLocale), vars) : ''
  const subject = def.subject ? interpolate(pickLocale(channel.subject, lang, defaultLocale), vars) : ''
  return def.href(target, text, subject)
}
